import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

type BannerConfig = { type: 'image' | 'video'; url: string };

export const Hero: React.FC = () => {
  const [banners, setBanners] = useState<BannerConfig[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'siteConfig', 'banners'));
        if (!snap.exists()) {
          setBanners([]);
          return;
        }
        const data = snap.data() as any;
        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items
          .map((it: any) => ({
            type: it?.type === 'video' ? 'video' : 'image',
            url: typeof it?.url === 'string' ? it.url.trim() : '',
          }))
          .filter((it: any) => it.url);
        setBanners(normalized);
        setActiveIndex(0);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;
    const timer = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % banners.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [banners.length, isPaused]);

  const activeBanner = banners.length > 0 ? banners[activeIndex % banners.length] : null;

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000); // Resume auto-slide after 10s
  };

  const nextSlide = () => {
    goToSlide((activeIndex + 1) % banners.length);
  };

  const prevSlide = () => {
    goToSlide((activeIndex - 1 + banners.length) % banners.length);
  };

  return (
    <div className="w-full bg-white text-stone-900 mb-6 border-b border-orange-100">
      {activeBanner ? (
        <div className="container mx-auto px-4 py-4">
          <div className="relative w-full h-[180px] sm:h-[240px] md:h-[300px] rounded-2xl overflow-hidden border border-orange-200 shadow-sm bg-orange-50">
            {activeBanner.type === 'video' ? (
              <video key={activeBanner.url} src={activeBanner.url} className="w-full h-full object-cover" autoPlay muted loop playsInline />
            ) : (
              <img src={activeBanner.url} alt="Banner" className="w-full h-full object-cover" />
            )}

            {/* Navigation Controls */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white text-stone-900 rounded-full shadow-lg transition-all"
                  aria-label="Previous"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white text-stone-900 rounded-full shadow-lg transition-all"
                  aria-label="Next"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Navigation Dots */}
          {banners.length > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === activeIndex
                      ? 'bg-orange-600 w-8'
                      : 'bg-orange-200 hover:bg-orange-300'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="container mx-auto px-4 py-2" />
      )}
    </div>
  );
};